<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-start justify-center pt-10 bg-black/60 sm:items-center sm:pt-0"
    @click="handleClickOutside"
  >
    <div
      class="relative w-full h-auto max-h-[90vh] p-4 overflow-y-auto bg-white rounded-xl shadow-2xl sm:w-1/2 sm:p-8"
    >
      <div class="flex items-center justify-between mb-8">
        <h2 class="text-2xl font-bold">{{ title }}</h2>
        <button
          type="button"
          aria-label="Close"
          @click="closeModal"
          class="transition text-neutral-400 hover:text-neutral-700"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-7 h-7"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
      <form class="space-y-7" @submit.prevent="submit">
        <!-- Debug Info (disable in production) -->
        <div v-if="false" class="p-2 bg-yellow-100 rounded text-xs mb-4">
          <strong>Debug Info:</strong><br />
          Gallery prop: {{ gallery ? "Exists" : "null" }}<br />
          Gallery data: {{ JSON.stringify(gallery, null, 2) }}<br />
          Form data: {{ JSON.stringify(form.data(), null, 2) }}
        </div>

        <div>
          <label
            for="title"
            class="block mb-2 text-sm font-medium text-gray-900"
          >
            Judul Gallery
          </label>
          <input
            id="title"
            type="text"
            v-model="form.title"
            class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
            placeholder="Masukkan judul gallery"
            required
          />
          <InputError :message="form.errors.title" class="mt-2" />
        </div>

        <div>
          <label
            for="description"
            class="block mb-2 text-sm font-medium text-gray-900"
          >
            Deskripsi
          </label>
          <textarea
            id="description"
            v-model="form.description"
            rows="4"
            class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
            placeholder="Masukkan deskripsi gallery"
          ></textarea>
          <InputError :message="form.errors.description" class="mt-2" />
        </div>

        <div>
          <label
            for="status"
            class="block mb-2 text-sm font-medium text-gray-900"
          >
            Status
          </label>
          <select
            id="status"
            v-model="form.status"
            class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
          >
            <option value="">Pilih Status</option>
            <option value="1">Aktif</option>
            <option value="0">Nonaktif</option>
          </select>
          <InputError :message="form.errors.status" class="mt-2" />
        </div>

        <div class="flex space-x-4">
          <button
            type="submit"
            :disabled="form.processing"
            class="flex-1 inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <i class="mr-2 fa-solid fa-floppy-disk"></i>
            {{ form.processing ? "Menyimpan..." : "Simpan Gallery" }}
          </button>

          <button
            type="button"
            @click="closeModal"
            class="px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
          >
            Batal
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import InputError from "@/Components/InputError.vue";
import PrimaryBtn from "@/Components/PrimaryBtn.vue";
import InputField from "@/Components/InputField.vue";
import InputLabel from "@/Components/InputLabel.vue";

import { defineProps, ref, computed, watch } from "vue";
import Swal from "sweetalert2";
import { useForm } from "@inertiajs/vue3";

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  gallery: {
    type: Object,
    default: () => null,
  },
});

const emit = defineEmits(["update:isOpen"]);

const form = useForm({
  id: null,
  idgallery: null,
  title: "",
  description: "",
  status: "",
});

// Watch for gallery data changes and populate form
watch(
  () => props.gallery,
  (newGallery) => {
    console.log("Gallery data received in modal:", newGallery); // Debug log
    if (newGallery && typeof newGallery === "object") {
      // Handle various possible field names for ID
      const galleryId = newGallery.idgallery || newGallery.id || null;

      form.id = newGallery.id || galleryId;
      form.idgallery = galleryId;
      form.title = newGallery.title || newGallery.judul || "";
      form.description = newGallery.description || newGallery.deskripsi || "";

      // Handle status conversion - ensure it's a string
      let status = newGallery.status;
      if (status !== undefined && status !== null) {
        status = String(status);
      } else {
        status = "";
      }
      form.status = status;

      console.log("Form populated with:", form.data()); // Debug log
    } else {
      console.log("No valid gallery data received"); // Debug log
    }
  },
  { immediate: true, deep: true }
);

// Also watch when modal opens to ensure data is loaded
watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen && props.gallery) {
      const gallery = props.gallery;
      console.log("Modal opened with gallery:", gallery); // Debug log

      // Handle various possible field names for ID
      const galleryId = gallery.idgallery || gallery.id || null;

      form.id = gallery.id || galleryId;
      form.idgallery = galleryId;
      form.title = gallery.title || gallery.judul || "";
      form.description = gallery.description || gallery.deskripsi || "";

      // Handle status conversion - ensure it's a string
      let status = gallery.status;
      if (status !== undefined && status !== null) {
        status = String(status);
      } else {
        status = "";
      }
      form.status = status;

      console.log("Form updated on modal open:", form.data()); // Debug log
    } else if (isOpen && !props.gallery) {
      console.log("Modal opened but no gallery data provided"); // Debug log
    }
  }
);

const closeModal = () => {
  // Clear form errors and reset form
  form.clearErrors();
  form.reset();
  emit("update:isOpen", false);
};

const handleClickOutside = (event) => {
  if (event.target === event.currentTarget) {
    closeModal();
  }
};

const submit = () => {
  // Validate that we have the necessary ID
  if (!form.idgallery && !form.id) {
    Swal.fire({
      title: "Error!",
      text: "ID gallery tidak ditemukan",
      icon: "error",
      confirmButtonText: "OK",
    });
    return;
  }

  const galleryId = form.idgallery || form.id;
  console.log("Submitting form with ID:", galleryId, "Data:", form.data()); // Debug log

  form.put(route("gallery.update", galleryId), {
    onSuccess: () => {
      Swal.fire({
        title: "Berhasil!",
        text: "Gallery berhasil diperbarui",
        icon: "success",
        confirmButtonText: "OK",
      });
      closeModal();
      // Refresh the page to show updated data
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    },
    onError: (errors) => {
      console.error("Update error:", errors); // Debug log
      Swal.fire({
        title: "Error!",
        text:
          "Gagal memperbarui gallery: " +
          (errors.message || "Terjadi kesalahan"),
        icon: "error",
        confirmButtonText: "OK",
      });
    },
    preserveScroll: true,
  });
};
</script>
